import React, { FC } from 'react';
import { createFeature } from '@edreamsodigeo/frontend-core';
import { Flex, Col, FlexRow } from '@edreamsodigeo/prisma-design-system';
import { useItinerary } from '@components/Results/Components/Itinerary/Provider';
import {
  PRICE_FREEZE_MOBILE,
  HOTEL_VOUCHER,
  FAVORITES_RESULTS,
} from 'clientState/ab/aliases';
import { useIntersectionObserver } from '@components/Results/Components/Itinerary/PersuasiveMessages/useIntersectionObserver';
import { usePriceFreezePromo } from '@components/Results/Components/Itinerary/PersuasiveMessages/PersuasiveMessages';
import { usePrime } from 'clientState/providers/PrimeProvider';
import useEnabledItineraryPersuasiveTPills from 'clientState/hooks/useEnabledItineraryPills';
import {
  CarbonFootprintPill,
  CheapestPill,
  FastestPill,
  EarliestPill,
  GeniusPill,
  HotelVoucher,
  NightsPill,
  PrimeDayPill,
  PriceFreezeVersionedComponentTag,
  PillsProps,
} from '@components/Results/Components/ThreeColumnsItinerary/PersuasiveTags/Pills';
import Favorite from '@components/Favorites/Favorite';
import { useEqualProminencePrimeDisplayMobileInResults } from 'clientState/hooks/useEqualProminencePrimeDisplayMobileInResults';
import { HighlightedType } from '../../ItineraryHeader/constants/highlightedTypes';

type PersuasiveTagsComponent = {
  hasRetailingTechniques: boolean;
  itineraryId: string | undefined;
  isUnavailableItinerary?: boolean;
};

export const pillComponents: Record<string, FC<PillsProps>> = {
  recommended: GeniusPill,
  cheapest: CheapestPill,
  fastest: FastestPill,
  earliest: EarliestPill,
  numNights: NightsPill,
  carbonFootprint: CarbonFootprintPill,
  primeDay: PrimeDayPill,
  hotelVoucher: HotelVoucher,
};

export type PillTypes =
  | 'primeDay'
  | 'recommended'
  | 'cheapest'
  | 'fastest'
  | 'earliest'
  | 'hotelVoucher'
  | 'priceFreeze'
  | 'numNights'
  | 'carbonFootprint';

const FavoriteComponent = createFeature({
  name: FAVORITES_RESULTS,
  variations: [null, Favorite],
  version: 1,
});

const NoPersuasiveTagsGapOnEqualProminence: FC<{
  pillsQty: number;
  highlightedType: HighlightedType | undefined;
  isEqualProminencePrimeDisplayMobileInResults: boolean;
}> = ({
  pillsQty,
  highlightedType,
  isEqualProminencePrimeDisplayMobileInResults,
}) => {
  return highlightedType !== HighlightedType.Default &&
    isEqualProminencePrimeDisplayMobileInResults &&
    pillsQty === 0 ? (
    <Flex
      data-testid="no-persuasive-tags-gap-eqlprom-in-results"
      height={'4px'}
    />
  ) : null;
};

const PersuasiveTagsWithPriceFreezeComponent: FC<PersuasiveTagsComponent> = ({
  hasRetailingTechniques = true,
  itineraryId,
  isUnavailableItinerary,
}) => {
  const isEqualProminencePrimeDisplayMobileInResults =
    useEqualProminencePrimeDisplayMobileInResults();
  const { highlightedType } = useItinerary();
  const isSem = window?.location.href.includes('/bestprices');
  const { isPrimeUser = false } = usePrime() || {};
  const {
    available: isPriceFreezeAvailable,
    loading: loadingPriceFreeze,
    deposit,
  } = usePriceFreezePromo();

  const pillsToDisplay: PillTypes[] = useEnabledItineraryPersuasiveTPills(
    isSem,
    isPriceFreezeAvailable,
    loadingPriceFreeze,
    hasRetailingTechniques
  );

  const sortedDisplayedPills: JSX.Element[] = pillsToDisplay.map((pill) => {
    switch (pill) {
      case 'primeDay':
      case highlightedType: {
        const HighlightedPill = pillComponents[pill];
        return (
          <HighlightedPill
            key={pill}
            isUnavailableItinerary={isUnavailableItinerary}
          />
        );
      }
      case 'priceFreeze':
        return (
          <PriceFreezeVersionedComponentTag
            isPriceFreezeAvailable={isPriceFreezeAvailable}
            loading={loadingPriceFreeze}
            deposit={deposit}
            isPrimeUser={isPrimeUser}
            isUnavailableItinerary={isUnavailableItinerary}
          />
        );
      default: {
        const Pill = pillComponents[pill];
        return <Pill isUnavailableItinerary={isUnavailableItinerary} />;
      }
    }
  });

  return (
    <FlexRow
      flexDirection="row"
      justifyContent="space-between"
      alignItems="start"
    >
      <Col>
        <div data-testid="persuasive-tags-price-freeze">
          {sortedDisplayedPills.length > 0 && (
            <Flex
              gap="1x_small"
              alignItems={'start'}
              flexWrap={'wrap'}
              mb={
                isEqualProminencePrimeDisplayMobileInResults
                  ? '2x_small'
                  : 'small'
              }
            >
              {sortedDisplayedPills[0]}
              {sortedDisplayedPills[1]}
            </Flex>
          )}
          <NoPersuasiveTagsGapOnEqualProminence
            pillsQty={sortedDisplayedPills.length}
            highlightedType={highlightedType}
            isEqualProminencePrimeDisplayMobileInResults
          />
        </div>
      </Col>
      <Col>
        <FavoriteComponent
          itineraryId={itineraryId}
          withRetailPills={sortedDisplayedPills.length > 0}
        />
      </Col>
    </FlexRow>
  );
};

const PersuasiveTagsWithPriceFreezeAndHotelVoucherComponent: FC<
  PersuasiveTagsComponent
> = ({
  hasRetailingTechniques = true,
  itineraryId,
  isUnavailableItinerary,
}) => {
  const isEqualProminencePrimeDisplayMobileInResults =
    useEqualProminencePrimeDisplayMobileInResults();
  const { highlightedType } = useItinerary();
  const isSem = window?.location.href.includes('/bestprices');
  const { isPrimeUser = false } = usePrime() || {};
  const {
    available: isPriceFreezeAvailable,
    loading: loadingPriceFreeze,
    deposit,
  } = usePriceFreezePromo();
  const pillsToDisplay: PillTypes[] = useEnabledItineraryPersuasiveTPills(
    isSem,
    isPriceFreezeAvailable,
    loadingPriceFreeze,
    hasRetailingTechniques
  );

  const sortedDisplayedPills: JSX.Element[] = pillsToDisplay.map((pill) => {
    switch (pill) {
      case 'primeDay':
      case highlightedType: {
        const HighlightedPill = pillComponents[pill];
        return (
          <HighlightedPill
            key={pill}
            isUnavailableItinerary={isUnavailableItinerary}
          />
        );
      }
      case 'priceFreeze':
        return (
          <PriceFreezeVersionedComponentTag
            isPriceFreezeAvailable={isPriceFreezeAvailable}
            loading={loadingPriceFreeze}
            deposit={deposit}
            isPrimeUser={isPrimeUser}
            isUnavailableItinerary={isUnavailableItinerary}
          />
        );
      case 'hotelVoucher': {
        const HotelVoucher = pillComponents[pill];
        return <HotelVoucher isUnavailableItinerary={isUnavailableItinerary} />;
      }
      default: {
        const Pill = pillComponents[pill];
        return <Pill isUnavailableItinerary={isUnavailableItinerary} />;
      }
    }
  });

  // We allow to show 3 pills only if the third pill is hotelVoucher
  // That could only happen if Freeze pill is also displayed
  const showThreePills = pillsToDisplay[2] === 'hotelVoucher';

  return (
    <FlexRow
      flexDirection="row"
      justifyContent="space-between"
      alignItems="start"
    >
      <Col>
        <div data-testid="persuasive-tags-price-freeze-hotel-voucher">
          {sortedDisplayedPills.length > 0 && (
            <Flex
              gap="1x_small"
              alignItems={'start'}
              flexWrap={'wrap'}
              mb={
                isEqualProminencePrimeDisplayMobileInResults
                  ? '2x_small'
                  : 'small'
              }
            >
              {sortedDisplayedPills[0]}
              {sortedDisplayedPills[1]}
              {showThreePills && sortedDisplayedPills[2]}
            </Flex>
          )}
          <NoPersuasiveTagsGapOnEqualProminence
            pillsQty={sortedDisplayedPills.length}
            highlightedType={highlightedType}
            isEqualProminencePrimeDisplayMobileInResults
          />
        </div>
      </Col>
      <Col>
        <FavoriteComponent
          itineraryId={itineraryId}
          withRetailPills={sortedDisplayedPills.length > 0}
        />
      </Col>
    </FlexRow>
  );
};

const PersuasiveTagsHotelVoucher: FC<PersuasiveTagsComponent> = ({
  hasRetailingTechniques = true,
  itineraryId,
  isUnavailableItinerary,
}) => {
  const isEqualProminencePrimeDisplayMobileInResults =
    useEqualProminencePrimeDisplayMobileInResults();
  const { highlightedType } = useItinerary();
  const isSem = window?.location.href.includes('/bestprices');
  const pillsToDisplay: PillTypes[] = useEnabledItineraryPersuasiveTPills(
    isSem,
    false,
    false,
    hasRetailingTechniques
  );

  const sortedDisplayedPills: JSX.Element[] = pillsToDisplay.map((pill) => {
    switch (pill) {
      case 'primeDay':
      case highlightedType: {
        const HighlightedPill = pillComponents[pill];
        return (
          <HighlightedPill
            key={pill}
            isUnavailableItinerary={isUnavailableItinerary}
          />
        );
      }
      case 'hotelVoucher': {
        const HotelVoucher = pillComponents[pill];
        return <HotelVoucher isUnavailableItinerary={isUnavailableItinerary} />;
      }
      default: {
        const Pill = pillComponents[pill];
        return <Pill isUnavailableItinerary={isUnavailableItinerary} />;
      }
    }
  });

  return (
    <FlexRow
      flexDirection="row"
      justifyContent="space-between"
      alignItems="start"
    >
      <Col>
        <div data-testid="persuasive-tags-hotel-voucher">
          {sortedDisplayedPills.length > 0 && (
            <Flex
              gap="1x_small"
              alignItems={'start'}
              flexWrap={'wrap'}
              mb={
                isEqualProminencePrimeDisplayMobileInResults
                  ? '2x_small'
                  : 'small'
              }
            >
              {sortedDisplayedPills[0]}
              {sortedDisplayedPills[1]}
            </Flex>
          )}
          <NoPersuasiveTagsGapOnEqualProminence
            pillsQty={sortedDisplayedPills.length}
            highlightedType={highlightedType}
            isEqualProminencePrimeDisplayMobileInResults
          />
        </div>
      </Col>
      <Col>
        <FavoriteComponent
          itineraryId={itineraryId}
          withRetailPills={sortedDisplayedPills.length > 0}
        />
      </Col>
    </FlexRow>
  );
};

const PersuasiveTagsBase: FC<PersuasiveTagsComponent> = ({
  hasRetailingTechniques = true,
  itineraryId,
  isUnavailableItinerary,
}) => {
  const { highlightedType } = useItinerary();
  const isSem = window?.location.href.includes('/bestprices');
  const pillsToDisplay: PillTypes[] = useEnabledItineraryPersuasiveTPills(
    isSem,
    false,
    false,
    hasRetailingTechniques
  );
  const isEqualProminencePrimeDisplayMobileInResults =
    useEqualProminencePrimeDisplayMobileInResults();

  const sortedDisplayedPills: JSX.Element[] = pillsToDisplay.map((pill) => {
    switch (pill) {
      case 'primeDay':
      case highlightedType: {
        const HighlightedPill = pillComponents[pill];
        return (
          <HighlightedPill
            key={pill}
            isUnavailableItinerary={isUnavailableItinerary}
          />
        );
      }
      default: {
        const Pill = pillComponents[pill];
        return <Pill isUnavailableItinerary={isUnavailableItinerary} />;
      }
    }
  });

  return (
    <FlexRow
      flexDirection="row"
      justifyContent="space-between"
      alignItems="start"
    >
      <Col>
        <div data-testid="persuasive-tags">
          {sortedDisplayedPills.length > 0 && hasRetailingTechniques && (
            <Flex
              gap="1x_small"
              alignItems={'start'}
              flexWrap={'wrap'}
              mb={
                isEqualProminencePrimeDisplayMobileInResults
                  ? '2x_small'
                  : 'small'
              }
            >
              {sortedDisplayedPills[0]}
              {sortedDisplayedPills[1]}
            </Flex>
          )}
          <NoPersuasiveTagsGapOnEqualProminence
            pillsQty={sortedDisplayedPills.length}
            highlightedType={highlightedType}
            isEqualProminencePrimeDisplayMobileInResults
          />
        </div>
      </Col>
      <Col>
        <FavoriteComponent
          itineraryId={itineraryId}
          withRetailPills={sortedDisplayedPills.length > 0}
        />
      </Col>
    </FlexRow>
  );
};

const PersuasiveTagsWithPriceFreezeWithHotelVoucherABComponent = createFeature({
  name: HOTEL_VOUCHER,
  variations: [
    PersuasiveTagsWithPriceFreezeComponent,
    PersuasiveTagsWithPriceFreezeAndHotelVoucherComponent,
  ],
  version: 1,
});

const PersuasiveTagsWithPriceFreeze: FC<PersuasiveTagsComponent> = ({
  hasRetailingTechniques,
  itineraryId,
}): JSX.Element => {
  const { ref, visible } = useIntersectionObserver();
  return (
    <div ref={ref as React.MutableRefObject<HTMLDivElement>}>
      {visible && (
        <PersuasiveTagsWithPriceFreezeWithHotelVoucherABComponent
          hasRetailingTechniques={hasRetailingTechniques}
          itineraryId={itineraryId}
        />
      )}
    </div>
  );
};

const PersuasiveTagsBaseWithHotelVoucher = createFeature({
  name: HOTEL_VOUCHER,
  variations: [PersuasiveTagsBase, PersuasiveTagsHotelVoucher],
  version: 1,
});

const PersuasiveTags = createFeature({
  name: PRICE_FREEZE_MOBILE,
  variations: [
    PersuasiveTagsBaseWithHotelVoucher,
    PersuasiveTagsWithPriceFreeze,
    PersuasiveTagsWithPriceFreeze,
    PersuasiveTagsWithPriceFreeze,
  ],
  version: 1,
});

export default PersuasiveTags;
